import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Icon,
  FormControl,
  useToast,
  Box,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaCalendarAlt,
  FaTags,
  FaLayerGroup,
  FaUserTie,
  FaChalkboardTeacher,
  FaUsers,
  FaUser,
} from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const StudentEventRegistration = () => {
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  // State variables for form inputs and fetched data
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [studentsData, setStudentsData] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);

  const [selectedEvent, setSelectedEvent] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [trainer, setTrainer] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");

  const [classes, setClasses] = useState([]);
  const [divisions, setDivisions] = useState([]);

  // Session data for school, user, and student
  const schoolId = sessionStorage.getItem("school_id");
  const userId = sessionStorage.getItem("user_id");
  const studentId = sessionStorage.getItem("student_id");

  // Fetch all events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/events/event/list_all_event.php?school_id=${schoolId}`
        );
        if (response.data.success) setEvents(response.data.data.events || []);
      } catch (error) {
        console.error("Error fetching events:", error);
        toast({
          title: "Error Fetching Events",
          description: "An error occurred while fetching events.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchEvents();
  }, [schoolId]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/events/category/list_category.php?school_id=${schoolId}`
        );
        if (response.data.success) setCategories(response.data.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast({
          title: "Error Fetching Categories",
          description: "An error occurred while fetching categories.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchCategories();
  }, [schoolId]);

  // Fetch subcategories for the selected category
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!category) return;
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/events/sub_category/list_subcat.php?school_id=${schoolId}`
        );

        if (response.data.success) {
          const subcategoriesForCategory = response.data.data
            .flatMap((event) => event.categories)
            .filter((cat) => cat.category_id === parseInt(category, 10))
            .flatMap((cat) => cat.sub_categories || []);
          setFilteredSubcategories(subcategoriesForCategory);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        toast({
          title: "Error Fetching Subcategories",
          description: "An error occurred while fetching subcategories.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchSubcategories();
  }, [category, schoolId]);

  useEffect(() => {
    if (!subcategory) {
      setTrainer("");
      return;
    }
    // Find the allocated teacher for the selected subcategory
    const selectedSubcat = filteredSubcategories.find(
      (subcat) => subcat.subcat_id === parseInt(subcategory, 10)
    );
    setTrainer(selectedSubcat?.allocated_teacher || "");
  }, [subcategory, filteredSubcategories]);

  // Filter categories based on the selected event
  useEffect(() => {
    if (selectedEvent) {
      setFilteredCategories(
        categories.filter((cat) => cat.event_id === parseInt(selectedEvent, 10))
      );
    } else {
      setFilteredCategories([]);
    }
    setCategory("");
    setSubcategory("");
    setTrainer("");
  }, [selectedEvent, categories]);

  // Fetch classes and divisions
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/class/class_master.php`
        );
        if (response.data.success) setClasses(response.data.data);
      } catch (error) {
        console.error("Error fetching classes:", error);
      }
    };
    fetchClasses();
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/class/division.php`
        );
        if (response.data.success) setDivisions(response.data.data);
      } catch (error) {
        console.error("Error fetching divisions:", error);
      }
    };
    fetchDivisions();
  }, []);

  // Fetch students based on selected class and division
  useEffect(() => {
    if (selectedClass && selectedDivision) {
      // Log the parameters before sending the request
      console.log(
        "Fetching students with class_id:",
        selectedClass,
        "and division_id:",
        selectedDivision
      );

      axios
        .post(`${BASE_URL}/admin/exam/depending_student.php`, {
          class_id: selectedClass,
          division_id: selectedDivision,
        })
        .then((response) => {
          // Log the response data
          console.log("Students data received:", response.data);

          // Set the students data to state
          setStudentsData(response.data.data || []);
        })
        .catch((error) => {
          // Log the error if the request fails
          console.error("Error fetching students:", error);
        });
    }
  }, [selectedClass, selectedDivision]);

  // Filter students based on search term
  useEffect(() => {
    const filtered = studentsData.filter((student) => {
      const matchesName = student.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesName;
    });
    setFilteredStudents(filtered);
  }, [searchTerm, studentsData]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchTerm(query);
  };

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
  };

  const handleDivisionChange = (e) => {
    setSelectedDivision(e.target.value);
  };

  // Handle registration submission
  const handleSubmit = async () => {
    // Assuming `classes` and `divisions` have already been populated
    const selectedClassInfo = classes.find(
      (cls) => cls.id === parseInt(selectedClass, 10)
    );
    const selectedDivisionInfo = divisions.find(
      (div) => div.id === parseInt(selectedDivision, 10)
    );

    const registrationData = {
      user_id: userId,
      school_id: schoolId,
      event_id: selectedEvent,
      category_id: category,
      subcategory_id: subcategory,
      student_id: selectedStudent || studentId,
      class_id: selectedClass,
      class_name: selectedClassInfo ? selectedClassInfo.class : "", // Pass class name
      division_id: selectedDivision,
      division_name: selectedDivisionInfo ? selectedDivisionInfo.division : "", // Pass division name
    };

    console.log("submit create", registrationData);

    try {
      const response = await axios.post(
        `${BASE_URL}/admin/events/registration/create_reg.php`,
        registrationData
      );
      if (response.data.success) {
        toast({
          title: "Registration Successful",
          description: "Your registration was successful.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      } else {
        toast({
          title: "Error",
          description: "Error: " + response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error submitting registration:", error);
      toast({
        title: "Error Submitting Registration",
        description: "An error occurred while submitting the registration.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Button
        leftIcon={<FaPlus />}
        colorScheme="purple"
        onClick={onOpen}
        display={{ base: "flex", md: "inline-flex" }} // Hide text on mobile (base breakpoint)
      >
        <Box display={{ base: "none", md: "inline" }}>Category</Box>
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            {" "}
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Student Event Registration
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl id="eventTitle">
                <FormLabel>
                  <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                  Event Title
                </FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select Event"
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                >
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.event_title}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl id="category">
                <FormLabel>
                  <Icon as={FaTags} mr={1} color="teal.500" />
                  Category
                </FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  isDisabled={!selectedEvent}
                >
                  {filteredCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl id="subcategory">
                <FormLabel>
                  <Icon as={FaLayerGroup} mr={1} color="teal.500" />
                  Subcategory
                </FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select Subcategory"
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  isDisabled={!category}
                >
                  {filteredSubcategories.map((subcat) => (
                    <option key={subcat.subcat_id} value={subcat.subcat_id}>
                      {subcat.sub_category}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl id="allocatedTeacher">
                <FormLabel>
                  <Icon as={FaUserTie} mr={1} color="teal.500" />
                  Allocated Teacher
                </FormLabel>
                <Input value={trainer} isReadOnly />
              </FormControl>
            </SimpleGrid>

            <FormControl id="class">
              <FormLabel>
                {" "}
                <Icon as={FaChalkboardTeacher} mt={2} mr={1} color="teal.500" />
                Class
              </FormLabel>
              <Select
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                placeholder="Select Class"
                value={selectedClass}
                onChange={handleClassChange}
              >
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.class}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl id="division">
              <FormLabel>
                <Icon as={FaUsers} mt={2} mr={1} color="teal.500" />
                Division
              </FormLabel>
              <Select
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                placeholder="Select Division"
                value={selectedDivision}
                onChange={handleDivisionChange}
              >
                {divisions.map((div) => (
                  <option key={div.id} value={div.id}>
                    {div.division}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl id="student">
              <FormLabel>
                <Icon as={FaUser} mt={2} mr={1} color="teal.500" />
                Student
              </FormLabel>
              <Select
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                placeholder="Select Student"
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
              >
                {filteredStudents.length > 0 ? (
                  filteredStudents.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name}
                    </option>
                  ))
                ) : (
                  <option disabled>No students available</option>
                )}
              </Select>
            </FormControl>

            <Divider mt={5} />
            {/* <FormControl mt={6} id="studentSearch">
              <FormLabel>Search Student by Name</FormLabel>
              <Input
                placeholder="Enter Student Name"
                value={searchTerm}
                onChange={handleSearch}
              />
            </FormControl> */}
          </ModalBody>

          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
            {/* <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default StudentEventRegistration;
